/*
 * cáene · Smart Cart routes
 *
 * Two small jobs around Rebuy's Smart Cart, and nothing else.
 *
 *   A. The ritual's completed message links to the native cart page. When Smart
 *      Cart is ready we open that instead and leave the customer where they
 *      are. The href stays real, so with JavaScript off, with Rebuy missing, or
 *      on a modified click, the link navigates exactly as it does today.
 *
 *   C. Closing Smart Cart leaves focus on the app's own close button, which is
 *      hidden by then. We put focus back on the control the customer used to
 *      open the drawer, and only when nothing else has claimed it.
 *
 * The header cart icon is deliberately untouched: the app already opens Smart
 * Cart from it, so a theme handler there would only be a third participant in a
 * click that already has two.
 *
 * Rebuy contracts used, all documented: SmartCart.status, SmartCart.show() and
 * the rebuy:smartcart.hide event. Nothing here adds to the cart, fetches,
 * polls, waits for an undocumented event, or changes the app's markup.
 */

(() => {
  /* Codex loads this once from the layout, but a section render in the theme
     editor can run the file again. Two sets of listeners would act twice on one
     click. */
  if (window.caeneSmartCartRoutes) return;
  window.caeneSmartCartRoutes = true;

  const HEADER_CART = '#cart-icon-bubble';
  const RITUAL_CTA = '[data-ritual-cta]';
  const RITUAL_LINK = '[data-ritual-message-link]';
  const PRODUCT_SUBMIT = '.product-form__submit';
  /* The app's own container. Read only, for where focus sits and whether the
     drawer is marked hidden; the theme already targets this id throughout
     caene-app-design.css. */
  const REBUY_CART = '#rebuy-cart';

  /* Everything a customer can press that ends with Smart Cart open. Rebuy binds
     its own handlers to some of these; we only watch them. */
  const OPENERS = [HEADER_CART, RITUAL_CTA, RITUAL_LINK, PRODUCT_SUBMIT].join(',');

  let lastOpener = null;
  let restoreScheduled = false;

  const smartCart = () => window.Rebuy?.SmartCart;

  const smartCartIsReady = () => {
    const cart = smartCart();
    return cart?.status === 'ready' && typeof cart.show === 'function';
  };

  /* Hidden from assistive technology, including through an ancestor. A closed
     Smart Cart is exactly this: measured after a settled Escape, its container
     carries aria-hidden="true" while its contents keep a real box offscreen. */
  const isAriaHidden = (el) => el.closest('[aria-hidden="true"]') !== null;

  /* Connected, enabled, exposed and actually laid out. Geometry alone is not
     enough in either direction here: Dawn's drawer has been measured active with
     a zero rectangle, and Rebuy's closed drawer keeps a full-height rectangle
     parked offscreen. So aria-hidden carries the visibility judgement and
     getClientRects only rules out what was never rendered. */
  const isUsable = (el) =>
    el instanceof HTMLElement &&
    el.isConnected &&
    !el.disabled &&
    !el.hidden &&
    !el.closest('[inert]') &&
    !isAriaHidden(el) &&
    el.getClientRects().length > 0;

  /* The ritual button disables itself the moment the add succeeds, so focusing
     it would strand the customer on a dead control. Its completed message link
     is the visible successor, and the header cart is the last resort because it
     exists on every page. */
  const resolveTarget = (opener) => {
    if (isUsable(opener)) return opener;

    const ritualLink = opener?.closest?.('caene-full-ritual')?.querySelector(RITUAL_LINK);
    if (isUsable(ritualLink)) return ritualLink;

    const header = document.querySelector(HEADER_CART);
    return isUsable(header) ? header : null;
  };

  /* Capture phase, because the app's handler may stop the event before it
     bubbles. This listener never calls preventDefault; it only remembers. */
  document.addEventListener(
    'click',
    (event) => {
      const opener = event.target instanceof Element ? event.target.closest(OPENERS) : null;
      if (opener) lastOpener = opener;
    },
    true
  );

  /* A. Ordinary left click on the completed link, nothing else. Ctrl, meta,
     shift, alt and middle click all keep their normal meaning, so opening the
     cart page in a new tab still works. */
  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;

    const link = event.target instanceof Element ? event.target.closest(RITUAL_LINK) : null;
    if (!link || link.target === '_blank') return;
    if (!smartCartIsReady()) return;

    /* show() first, preventDefault second. If the app throws we have not taken
       the click yet, and the link navigates to the cart page as before. */
    try {
      smartCart().show();
    } catch (error) {
      console.error('[caene-smart-cart-routes] Smart Cart could not open', error);
      return;
    }

    event.preventDefault();
  });

  /* The drawer is closed when its container is marked hidden. This is the reopen
     guard too: a drawer that is open again by the time our frame runs is not
     marked hidden, and its focus is the customer's, not ours to move. With no
     container on the page there is no drawer to be open. */
  const smartCartIsClosed = () => {
    const container = document.querySelector(REBUY_CART);
    if (!container) return true;
    return container.getAttribute('aria-hidden') === 'true';
  };

  /* Only restore when nobody else holds focus. Focus inside a closed drawer is
     stranded by definition, however solid its box measures. Focus on a usable
     element anywhere else belongs to the customer or to the app's own
     restoration, and both are left alone. */
  const focusIsLoose = () => {
    const active = document.activeElement;
    if (!active || active === document.body) return true;
    if (active.closest?.(REBUY_CART)) return true;
    return !isUsable(active);
  };

  /* C. */
  document.addEventListener('rebuy:smartcart.hide', () => {
    if (restoreScheduled) return;
    restoreScheduled = true;

    /* One frame, so the app has finished hiding its markup before we read it.
       One frame only; we never chase a late close. */
    requestAnimationFrame(() => {
      restoreScheduled = false;
      if (!smartCartIsClosed()) return;
      if (!focusIsLoose()) return;

      const target = resolveTarget(lastOpener);
      /* The app and Dawn already return focus to the header on their own after a
         header-opened close. Re-focusing the element that already has focus
         would be noise at best. */
      if (!target || target === document.activeElement) return;

      /* preventScroll: a restore must not jump the page to a control that
         scrolled out of view while the drawer was open. */
      target.focus({ preventScroll: true });
    });
  });
})();

/* Rebuy's documented theme-local selling-plan sandbox. This requests actual
   Shopify plan data without changing the store's integration settings. */
(() => {
  const enablePlans = () => {
    if (window.Rebuy?.shop) window.Rebuy.shop.shopify_selling_plans_enabled = true;
  };
  document.addEventListener('rebuy.loaded', enablePlans);
  enablePlans();
})();

/* English copy for this draft's Smart Cart only. Keep Rebuy's live amounts
   and event handlers intact; update text nodes only when a known label changes. */
(() => {
  const labels = new Map([
    ['1 PÅSE VARJE MÅNAD • 15% rabatt','Subscription · 1 bag every month · Save 15%'],
    ['2 PÅSAR VARANNAN MÅNAD • 20% rabatt','Subscription · 2 bags every 2 months · Save 20%'],
    ['Varukorg','Your bag'],['PARAS MED','Pairs well with'],
    ['BOOSTA UPP MED DESSA','Complete your routine'],['Lägg till','Add'],
    ['Gratis Frakt!','Free shipping'],['FRI FRAKT','free shipping'],
    ['Gratis Gåva!','Complimentary gift'],
    ['Desire Gummies är nu gratis i din varukorg!','Your complimentary desire gummies have been added.'],
    ['Gratis CÁENE Gummies! (Värde 299 kr)','Complimentary gummies'],
    ['Varukorgen är tom','Your bag is empty'],
    ['Lägg till dina favorit gummies till din varukorg','Add your favourite gummies to begin your routine.'],
    ['SHOPPA','Explore gummies'],['Checkout →','Checkout']
  ]);
  const translate = (text) => {
    const value = text.trim();
    if (labels.has(value)) return text.replace(value, labels.get(value));
    const shipping = value.match(/^Bara (.+) kvar till FRI FRAKT!$/);
    if (shipping) return `${shipping[1]} away from free shipping`;
    const shippingFragment = value.match(/^Bara (.+) kvar till$/);
    if (shippingFragment) return `${shippingFragment[1]} away from `;
    const gift = value.match(/^Du är bara (.+) från att få Desire Gummies gratis!$/);
    if (gift) return `${gift[1]} away from complimentary desire gummies`;
    const subtotal = value.match(/^Totalt \((\d+) produkt(?:er)?\)$/);
    if (subtotal) return `Subtotal (${subtotal[1]} ${subtotal[1] === '1' ? 'item' : 'items'})`;
    return text;
  };
  const attach = () => {
    const cart = document.querySelector('#rebuy-cart');
    if (!cart || cart.dataset.caeneCopyReady) return;
    cart.dataset.caeneCopyReady = 'true';
    const sync = () => {
      const walker = document.createTreeWalker(cart, NodeFilter.SHOW_TEXT);
      let node;
      while ((node = walker.nextNode())) {
        if (node.parentElement.closest('script,style')) continue;
        const next = translate(node.nodeValue);
        if (next !== node.nodeValue) node.nodeValue = next;
      }
    };
    new MutationObserver(sync).observe(cart, {childList:true,subtree:true,characterData:true});
    sync();
  };
  document.addEventListener('rebuy:smartcart.ready', attach);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', attach, {once:true});
  attach();
})();
